/**
 * Care Hero — the 5-point scale and the settlement pillars, as colour TONES.
 *
 * Care is the one hero that draws several hues inside its own surfaces: the
 * 1–5 mood / settlement scale (mood orbs, mood river bars, checkpoint scale
 * chips) and the four settlement pillars it scores per checkpoint. Each is a
 * tone (`tones.ts`), which owns its shades in both themes and whose text is
 * contrast-tested on the page, a card and its own tint (tones.spec.ts). The
 * web draws a tone through its CSS variables (`toneVars(tone)`) or classes
 * (`TONE_CLASSES` in @tonnie/ui); the phone through `TONE_HEX`.
 *
 * These used to be 500-shade hex tables (`CARE_EMOTION_SCALE`,
 * `CARE_SCALE_COLORS`, `CARE_PILLAR_PALETTE`, `CARE_SETTLEMENT_PILLAR_COLORS`):
 * one shade for both themes, reading under 4.5:1 as text.
 */

import type { Tone } from './tones';

/**
 * The Care 1–5 scale, struggling → amazing: red → orange → amber → green →
 * cyan, so the top of the scale reads calm and high on every surface (never
 * pink). Keyed by score.
 */
export const CARE_SCALE_TONE: Record<1 | 2 | 3 | 4 | 5, Tone> = {
  1: 'red',
  2: 'orange',
  3: 'amber',
  4: 'green',
  5: 'cyan',
};

/**
 * The settlement pillars' tones, keyed by the BACKEND pillar id (journey-web
 * `CareHeroBanner` / `CareJourneyPlan`, citizen-mobile `CareScreen` /
 * `CheckpointScreen` / `QuickCheckinScreen`).
 */
export const CARE_SETTLEMENT_PILLAR_TONE = {
  housing_stability: 'blue',
  schooling_childcare: 'violet',
  work_fit: 'amber',
  wellbeing_social: 'rose',
} as const satisfies Record<string, Tone>;

export type CareSettlementPillarId = keyof typeof CARE_SETTLEMENT_PILLAR_TONE;
